import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@shared/lib/auth";
import { Button, Input, Alert, Spinner } from "@shared/components/ui";
import { Logo } from "@shared/components/Logo";
import { isCorrectAppForRole, transferSessionToApp } from "@shared/lib/roleRedirect";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth.status === "authenticated") {
      navigate(from, { replace: true });
    }
  }, [auth.status, navigate, from]);

  if (auth.status === "authenticated") {
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);
    setLoading(true);

    try {
      const result = await auth.signIn({ email, password });
      if (!result.needsSetup && !result.requires2FA) {
        if (result.userRole && !isCorrectAppForRole(result.userRole)) {
          transferSessionToApp(result.userRole);
          return;
        }
        navigate(from, { replace: true });
      }
    } catch (err) {
      setErrors([err instanceof Error ? err.message : "Login failed"]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6">
      <Logo size="lg" className="mb-4" />
      <p className="mb-6 text-sm text-muted">Stay connected to their day</p>
      <h1 className="mb-6 text-xl font-semibold text-foreground">Parent Login</h1>

      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        <Alert items={errors} />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? <Spinner /> : "Sign In"}
        </Button>
      </form>
    </div>
  );
}
